/**
 * Custom Keyboard Builder
 * Full-page keyboard configurator with dynamic sub-stages
 */

(function() {
  'use strict';

  // State Management
  window.KeyboardBuilder = {
    currentStage: 1,
    selections: {
      kit: null,
      subStages: {},  // { 'back-plate': {...}, 'switch-plate': {...} }
      heUpgrade: false,  // Track HE upgrade selection
      heUpgradeProduct: null,  // HE product data if selected
      switches: null,
      keycaps: null,
      assembly: null
    },
    activeSubStages: [],  // Array of active sub-stage configs
    totalStages: 4,  // Dynamically calculated
    stageMapping: {},  // Maps display stage numbers to actual stage types
    layoutRequirements: {
      '60': 70,
      '65': 70,
      '75': 88,
      'TKL': 88,
      '96': 105,
      'Fullsize': 105
    },
    products: {
      switches: [],
      keycaps: []
    },
    filters: {
      layout: 'all',
      layoutType: 'all',
      switchType: 'all',
      keycapCompat: 'compatible',
      showOutOfStock: false,
      searchQuery: '',
      sortBy: 'name-az'
    },
    selectedLayoutPreference: null,
    isLoading: false,
    isInitialized: false
  };

  const KB = window.KeyboardBuilder;
  const data = window.KBBuilderData || {};

  // Button state tracking for pulse animations
  let previousStageComplete = false;
  let previousCartReady = false;

  // DOM Elements
  const elements = {
    loading: document.getElementById('kb-builder-loading'),
    stages: {},
    nav: document.getElementById('kb-nav'),
    navItems: null, // Will be set after nav is generated
    navSelections: {},
    productGrids: {},
    subStagesContainer: document.getElementById('kb-sub-stages-container'),
    summaryItems: document.getElementById('kb-summary-items'),
    summaryTotal: document.getElementById('kb-summary-total'),
    stageNav: document.getElementById('kb-stage-nav'),
    mobileNav: document.getElementById('kb-mobile-nav'),
    btnBack: null,
    btnNext: null,
    btnAddToCart: null,
    progressFill: document.getElementById('kb-progress-fill'),
    quickView: document.getElementById('kb-quick-view'),
    warningModal: document.getElementById('kb-warning-modal'),
    layoutModal: document.getElementById('kb-layout-modal'),
    // Mobile summary elements
    mobileSummary: document.getElementById('kb-mobile-summary'),
    mobileSummaryTrigger: document.getElementById('kb-mobile-summary-trigger'),
    mobileSummaryCount: document.getElementById('kb-mobile-summary-count'),
    mobileSummaryTotal: document.getElementById('kb-mobile-summary-total'),
    mobileSummaryBackdrop: document.getElementById('kb-mobile-summary-backdrop'),
    mobileSummaryDrawer: document.getElementById('kb-mobile-summary-drawer'),
    mobileSummaryBody: document.getElementById('kb-mobile-summary-body'),
    mobileSummaryClose: document.getElementById('kb-mobile-summary-close'),
    mobileSummaryDrawerTotal: document.getElementById('kb-mobile-summary-drawer-total')
  };

  // Initialize DOM element references for base stages
  for (let i = 1; i <= 4; i++) {
    elements.stages[i] = document.getElementById(`kb-stage-${i}`);
    elements.productGrids[i] = document.getElementById(`kb-products-${i}`);
  }

  // Utility Functions
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, data.moneyFormat);
    }
    const amount = (cents / 100).toFixed(2);
    return data.moneyFormat
      ? data.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      : `£${amount}`;
  }

  function debounce(fn, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  function capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  }

  function truncateText(text, maxLength) {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  function getSizedImage(url, size) {
    if (!url) return '';
    if (typeof url === 'object' && url.src) url = url.src;
    if (typeof url !== 'string') return '';
    return url.replace(/\.(png|jpg|jpeg|gif|webp)(\?.*)?$/i, `_${size}.$1$2`);
  }

  // =============================================
  // DYNAMIC STAGE MANAGEMENT
  // =============================================

  function getActiveSubStages(kitTitle) {
    if (!kitTitle || !data.barebonesSubStages) return [];

    const config = data.barebonesSubStages[kitTitle];
    if (!config) return [];

    return config.stages.filter(stage => {
      // Filter out stages that should be hidden when HE upgrade is selected
      if (stage.hideIfHE && KB.selections.heUpgrade) return false;
      // Hide HE choice stage entirely for ISO layouts (HE is ANSI-only)
      if (stage.type === 'he-choice' && KB.selectedLayoutPreference === 'iso') return false;
      return true;
    });
  }

  function recalculateStages() {
    const kitTitle = KB.selections.kit?.title?.replace(/\s*-\s*[^-]+$/, ''); // Remove variant suffix

    // Try to match with or without variant suffix
    let subStages = getActiveSubStages(kitTitle);
    if (subStages.length === 0 && KB.selections.kit) {
      // Try matching against product title stored in kit
      const productTitle = KB.selections.kit.productTitle || kitTitle;
      subStages = getActiveSubStages(productTitle);
    }

    KB.activeSubStages = subStages;

    // Calculate total stages: 1 (barebones) + sub-stages + remaining base stages
    // Base stages after sub-stages: switches (if not HE), keycaps, assembly
    let baseStagesAfter = KB.selections.heUpgrade ? 2 : 3; // keycaps + assembly OR switches + keycaps + assembly
    KB.totalStages = 1 + KB.activeSubStages.length + baseStagesAfter;

    // Build stage mapping
    KB.stageMapping = {};
    let stageNum = 1;

    // Stage 1 is always barebones
    KB.stageMapping[stageNum] = { type: 'barebones', id: 'barebones' };
    stageNum++;

    // Sub-stages
    KB.activeSubStages.forEach(subStage => {
      KB.stageMapping[stageNum] = { type: 'sub-stage', id: subStage.id, config: subStage };
      stageNum++;
    });

    // Switches (unless HE upgrade selected)
    if (!KB.selections.heUpgrade) {
      KB.stageMapping[stageNum] = { type: 'switches', id: 'switches' };
      stageNum++;
    }

    // Keycaps
    KB.stageMapping[stageNum] = { type: 'keycaps', id: 'keycaps' };
    stageNum++;

    // Assembly
    KB.stageMapping[stageNum] = { type: 'assembly', id: 'assembly' };

    // Regenerate navigation
    generateNavigation();

    // Render sub-stages
    renderSubStages();

    // Update totals display
    if (elements.totalSteps) {
      elements.totalSteps.textContent = KB.totalStages;
    }
  }

  function generateNavigation() {
    if (!elements.nav) return;

    let navHtml = '';

    for (let i = 1; i <= KB.totalStages; i++) {
      const mapping = KB.stageMapping[i];
      if (!mapping) continue;

      let label = '';
      let selectionText = '';
      let selectionId = '';

      switch (mapping.type) {
        case 'barebones':
          label = 'Barebones Kit';
          selectionText = KB.selections.kit ? truncateText(KB.selections.kit.title, 25) : 'Select a kit';
          selectionId = 'nav-selection-barebones';
          break;
        case 'sub-stage':
          label = mapping.config.navLabel || mapping.config.label;
          const subSelection = KB.selections.subStages[mapping.id];
          if (mapping.config.type === 'he-choice') {
            if (KB.selections.heUpgrade) {
              selectionText = 'Hall Effect Upgrade';
            } else if (KB.selections.subStages[mapping.id]?.selected === 'standard') {
              selectionText = 'Standard Mechanical';
            } else {
              selectionText = 'Choose option';
            }
          } else {
            selectionText = subSelection ? truncateText(subSelection.title, 20) : `Select ${label.toLowerCase()}`;
          }
          selectionId = `nav-selection-${mapping.id}`;
          break;
        case 'switches':
          label = 'Switches';
          selectionText = KB.selections.switches ? truncateText(KB.selections.switches.title, 25) : 'Select switches';
          selectionId = 'nav-selection-switches';
          break;
        case 'keycaps':
          label = 'Keycaps';
          selectionText = KB.selections.keycaps ? truncateText(KB.selections.keycaps.title, 25) : 'Select keycaps';
          selectionId = 'nav-selection-keycaps';
          break;
        case 'assembly':
          label = 'Assembly';
          selectionText = KB.selections.assembly ? KB.selections.assembly.title : 'Choose option';
          selectionId = 'nav-selection-assembly';
          break;
      }

      const isActive = i === KB.currentStage;
      const isCompleted = isStageCompleted(i);
      let isLocked = false;
      if (i > 1) {
        for (let j = 1; j < i; j++) {
          if (!isStageCompleted(j)) { isLocked = true; break; }
        }
      }

      navHtml += `
        <button class="kb-builder__nav-item ${isActive ? 'kb-builder__nav-item--active' : ''} ${isCompleted && !isActive ? 'kb-builder__nav-item--completed' : ''} ${isLocked ? 'kb-builder__nav-item--locked' : ''}"
                data-stage="${i}" data-stage-type="${mapping.type}" data-stage-id="${mapping.id}">
          <span class="kb-builder__nav-number">${i}</span>
          <div class="kb-builder__nav-content">
            <span class="kb-builder__nav-label">${label}</span>
            <span class="kb-builder__nav-selection" id="${selectionId}">${selectionText}</span>
          </div>
          <span class="kb-builder__nav-check">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
          </span>
        </button>
      `;
    }

    elements.nav.innerHTML = navHtml;

    // Re-attach nav listeners
    elements.navItems = elements.nav.querySelectorAll('.kb-builder__nav-item');
    attachNavListeners();
  }

  function renderSubStages() {
    if (!elements.subStagesContainer) return;

    if (KB.activeSubStages.length === 0) {
      elements.subStagesContainer.innerHTML = '';
      return;
    }

    let html = '';

    KB.activeSubStages.forEach((subStage, index) => {
      // Find actual stage number from stageMapping
      let actualStageNum = null;
      for (const [num, mapping] of Object.entries(KB.stageMapping)) {
        if (mapping.type === 'sub-stage' && mapping.id === subStage.id) {
          actualStageNum = parseInt(num);
          break;
        }
      }
      const stageNum = actualStageNum || (2 + index);

      if (subStage.type === 'he-choice') {
        html += renderHEChoiceStage(subStage, stageNum);
      } else if (subStage.type === 'product-select') {
        html += renderProductSelectStage(subStage, stageNum);
      }
    });

    elements.subStagesContainer.innerHTML = html;

    // Attach event listeners to sub-stage elements
    attachSubStageListeners();
  }

  function renderHEChoiceStage(config, stageNum) {
    const heProduct = data.subStageProducts?.[config.productHandle];
    const hePrice = heProduct?.variants?.[0]?.price || 0;
    const heAvailable = heProduct?.variants?.[0]?.available !== false;

    // HE upgrade is ANSI-only, check if ISO was selected
    const isIsoLayout = KB.selectedLayoutPreference === 'iso';

    const notesHtml = config.notes ? config.notes.map(note =>
      `<span class="kb-builder__he-warning-item"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>${note}</span>`
    ).join('') : '';

    // Determine HE option price text
    let hePriceText;
    if (!heAvailable) {
      hePriceText = 'Sold Out';
    } else if (isIsoLayout) {
      hePriceText = 'ANSI Only';
    } else {
      hePriceText = '+' + formatMoney(hePrice);
    }

    return `
      <section class="kb-builder__stage kb-builder__stage--sub" data-stage="${stageNum}" data-sub-stage-id="${config.id}" id="kb-sub-stage-${config.id}">
        <div class="kb-builder__stage-sticky">
          <div class="kb-builder__stage-header">
            <div class="kb-builder__stage-header-text">
              <h2 class="kb-builder__stage-title">${config.label}</h2>
              <p class="kb-builder__stage-description">Choose between standard mechanical switches or the Hall Effect upgrade module</p>
            </div>
            <div class="kb-builder__stage-nav"></div>
          </div>
        </div>

        <div class="kb-builder__he-choice" id="kb-he-choice">
          <div class="kb-builder__he-option ${!KB.selections.heUpgrade && KB.selections.subStages[config.id]?.selected === 'standard' ? 'kb-builder__he-option--selected' : ''}" data-he-choice="standard">
            <div class="kb-builder__he-option-header">
              <div class="kb-builder__he-option-radio">
                <input type="radio" name="he-choice" id="he-choice-standard" value="standard" ${!KB.selections.heUpgrade && KB.selections.subStages[config.id]?.selected === 'standard' ? 'checked' : ''}>
              </div>
              <div class="kb-builder__he-option-info">
                <span class="kb-builder__he-option-title">Standard Mechanical Switches</span>
                <span class="kb-builder__he-option-price">Included</span>
              </div>
            </div>
            <p class="kb-builder__he-option-desc">Use any compatible mechanical switch with your keyboard. Full flexibility in switch choice.</p>
          </div>

          <div class="kb-builder__he-option ${KB.selections.heUpgrade ? 'kb-builder__he-option--selected' : ''} ${!heAvailable || isIsoLayout ? 'kb-builder__he-option--unavailable' : ''}" data-he-choice="he">
            <div class="kb-builder__he-option-header">
              <div class="kb-builder__he-option-radio">
                <input type="radio" name="he-choice" id="he-choice-he" value="he" ${KB.selections.heUpgrade ? 'checked' : ''} ${!heAvailable || isIsoLayout ? 'disabled' : ''}>
              </div>
              <div class="kb-builder__he-option-info">
                <span class="kb-builder__he-option-title">Hall Effect Upgrade</span>
                <span class="kb-builder__he-option-price">${hePriceText}</span>
              </div>
            </div>
            <p class="kb-builder__he-option-desc">Includes Aluminium plate and GATERON KS-37B Magnetic Hall Sensor Switches. Plate selection options are removed with this upgrade.</p>
            ${isIsoLayout ? '<p class="kb-builder__he-note kb-builder__he-note--warning">Hall Effect upgrade is only compatible with ANSI layout.</p>' : ''}
            ${notesHtml ? `<div class="kb-builder__he-warnings">${notesHtml}</div>` : ''}
          </div>
        </div>
      </section>
    `;
  }

  function renderProductSelectStage(config, stageNum) {
    const product = data.subStageProducts?.[config.productHandle];

    if (!product) {
      return `
        <section class="kb-builder__stage kb-builder__stage--sub" data-stage="${stageNum}" data-sub-stage-id="${config.id}" id="kb-sub-stage-${config.id}">
          <div class="kb-builder__stage-sticky">
            <div class="kb-builder__stage-header">
              <div class="kb-builder__stage-header-text">
                <h2 class="kb-builder__stage-title">${config.label}</h2>
                <p class="kb-builder__stage-description">Select your preferred option</p>
              </div>
              <div class="kb-builder__stage-nav"></div>
            </div>
          </div>
          <div class="kb-builder__empty-state">
            <p>Product not available. Please contact support.</p>
          </div>
        </section>
      `;
    }

    // Build variant cards
    const selectedVariantId = KB.selections.subStages[config.id]?.variantId;
    let cardsHtml = '';

    product.variants.forEach(variant => {
      const isDefault = config.defaultVariant && variant.title.toLowerCase().includes(config.defaultVariant.toLowerCase());
      const showPrice = config.priceOverride !== 0 || !isDefault;
      const displayPrice = isDefault && config.priceOverride === 0 ? 0 : variant.price;
      const isSelected = selectedVariantId === variant.id;
      const isAvailable = variant.available || (config.ignoreStock && isDefault);

      cardsHtml += `
        <div class="kb-card kb-card--sub-stage ${isSelected ? 'kb-card--selected' : ''} ${!isAvailable ? 'kb-card--unavailable' : ''}"
             data-kb-sub-card
             data-sub-stage-id="${config.id}"
             data-product-id="${product.id}"
             data-variant-id="${variant.id}"
             data-price="${displayPrice}"
             data-title="${escapeHtml(product.title)}"
             data-variant-title="${escapeHtml(variant.title)}"
             data-is-default="${isDefault}"
             data-available="${isAvailable}">
          <div class="kb-card__image-wrapper">
            ${variant.featured_image
              ? `<img class="kb-card__image" src="${getSizedImage(variant.featured_image, '300x')}" alt="${escapeHtml(variant.title)}" loading="lazy">`
              : product.featured_image
                ? `<img class="kb-card__image" src="${getSizedImage(product.featured_image, '300x')}" alt="${escapeHtml(variant.title)}" loading="lazy">`
                : '<div class="kb-card__image-placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg></div>'
            }
            <div class="kb-card__badges">
              ${isDefault && config.priceOverride === 0 ? '<span class="kb-card__badge kb-card__badge--included">Included</span>' : ''}
              ${!isAvailable ? '<span class="kb-card__badge kb-card__badge--sold-out">Sold Out</span>' : ''}
            </div>
            <div class="kb-card__selected-indicator">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
            </div>
          </div>
          <div class="kb-card__content">
            <h3 class="kb-card__title">${escapeHtml(variant.title)}</h3>
            <div class="kb-card__price-row">
              <div class="kb-card__price">
                ${isDefault && config.priceOverride === 0
                  ? '<span class="kb-card__price-current kb-card__price-current--included">Included</span>'
                  : `<span class="kb-card__price-current">${displayPrice > 0 ? '+' + formatMoney(displayPrice) : formatMoney(0)}</span>`
                }
              </div>
            </div>
          </div>
          <button class="kb-card__select-btn" type="button" data-kb-sub-select ${!isAvailable ? 'disabled' : ''}>
            ${isAvailable ? '<span class="kb-card__select-btn-text">Select</span><span class="kb-card__select-btn-text--selected">Selected</span>' : '<span>Sold Out</span>'}
          </button>
        </div>
      `;
    });

    return `
      <section class="kb-builder__stage kb-builder__stage--sub" data-stage="${stageNum}" data-sub-stage-id="${config.id}" id="kb-sub-stage-${config.id}">
        <div class="kb-builder__stage-sticky">
          <div class="kb-builder__stage-header">
            <div class="kb-builder__stage-header-text">
              <h2 class="kb-builder__stage-title">${config.label}</h2>
              <p class="kb-builder__stage-description">Select your preferred ${config.label.toLowerCase()} option</p>
            </div>
            <div class="kb-builder__stage-nav"></div>
          </div>
        </div>

        <div class="kb-builder__products kb-builder__products--sub-stage" id="kb-sub-products-${config.id}">
          ${cardsHtml}
        </div>
      </section>
    `;
  }

  function attachSubStageListeners() {
    // HE Choice listeners
    const heChoice = document.getElementById('kb-he-choice');
    if (heChoice) {
      heChoice.querySelectorAll('.kb-builder__he-option').forEach(option => {
        option.addEventListener('click', () => {
          if (option.classList.contains('kb-builder__he-option--unavailable')) return;
          selectHEChoice(option.dataset.heChoice);
        });
      });

      heChoice.querySelectorAll('input[name="he-choice"]').forEach(radio => {
        radio.addEventListener('change', () => {
          selectHEChoice(radio.value);
        });
      });
    }

    // Sub-stage product card listeners
    document.querySelectorAll('[data-kb-sub-card]').forEach(card => {
      card.addEventListener('click', (e) => {
        if (!e.target.closest('[data-kb-sub-select]') && card.dataset.available !== 'false') {
          selectSubStageProduct(card);
        }
      });

      const selectBtn = card.querySelector('[data-kb-sub-select]');
      if (selectBtn) {
        selectBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          selectSubStageProduct(card);
        });
      }
    });
  }

  function selectHEChoice(choice) {
    const stageConfig = KB.activeSubStages.find(s => s.type === 'he-choice');
    if (!stageConfig) return;

    const wasHE = KB.selections.heUpgrade;
    KB.selections.heUpgrade = choice === 'he';

    if (KB.selections.heUpgrade) {
      // Get HE product data
      const heProduct = data.subStageProducts?.[stageConfig.productHandle];
      if (heProduct && heProduct.variants?.[0]) {
        KB.selections.heUpgradeProduct = {
          productId: heProduct.id,
          variantId: heProduct.variants[0].id,
          title: heProduct.title,
          price: heProduct.variants[0].price,
          image: heProduct.featured_image
        };
      }
      // Clear switches selection since HE replaces it
      KB.selections.switches = null;
      // Force ANSI layout for HE
      KB.selectedLayoutPreference = 'ansi';
    } else {
      KB.selections.heUpgradeProduct = null;
    }

    // Mark the HE choice stage as completed
    KB.selections.subStages[stageConfig.id] = {
      selected: choice,
      title: choice === 'he' ? 'Hall Effect Upgrade' : 'Standard Mechanical'
    };

    // Update UI for selected option
    document.querySelectorAll('.kb-builder__he-option').forEach(opt => {
      opt.classList.toggle('kb-builder__he-option--selected', opt.dataset.heChoice === choice);
      const radio = opt.querySelector('input[type="radio"]');
      if (radio) radio.checked = opt.dataset.heChoice === choice;
    });

    const stagesChanged = wasHE !== KB.selections.heUpgrade;

    // Re-render keycaps with ANSI filter if stages changed
    if (stagesChanged && KB.products.keycaps.length) {
      renderProducts(getStageNumber('keycaps'), 'keycaps');
    }

    refreshUI({ stagesChanged, selectionChanged: true });
  }

  function selectSubStageProduct(card) {
    const subStageId = card.dataset.subStageId;
    const productId = card.dataset.productId;
    const variantId = parseInt(card.dataset.variantId);
    const price = parseInt(card.dataset.price);
    const title = card.dataset.title;
    const variantTitle = card.dataset.variantTitle;
    const isDefault = card.dataset.isDefault === 'true';

    const img = card.querySelector('.kb-card__image');
    const image = img ? img.src : null;

    // Find stage config
    const stageConfig = KB.activeSubStages.find(s => s.id === subStageId);

    KB.selections.subStages[subStageId] = {
      productId,
      variantId,
      title: variantTitle !== 'Default Title' ? `${title} - ${variantTitle}` : title,
      variantTitle,
      price: isDefault && stageConfig?.priceOverride === 0 ? 0 : price,
      image,
      componentType: stageConfig?.label || subStageId,
      isIncluded: isDefault && stageConfig?.priceOverride === 0
    };

    // Update card selection state
    const container = document.getElementById(`kb-sub-products-${subStageId}`);
    if (container) {
      container.querySelectorAll('.kb-card').forEach(c => {
        c.classList.toggle('kb-card--selected', c === card);
      });
    }

    refreshUI({ selectionChanged: true });
  }

  function getStageNumber(stageType) {
    for (const [num, mapping] of Object.entries(KB.stageMapping)) {
      if (mapping.type === stageType) return parseInt(num);
    }
    return null;
  }

  // =============================================
  // STAGE NAVIGATION
  // =============================================

  function goToStage(stage) {
    if (stage < 1 || stage > KB.totalStages) return;
    if (stage > 1 && !KB.selections.kit) return;

    // Verify stage exists in mapping (prevents skipping sub-stages)
    if (!KB.stageMapping[stage]) return;

    // Check if we can navigate to this stage (all previous stages completed)
    for (let i = 1; i < stage; i++) {
      if (!isStageCompleted(i)) return;
    }

    // Clear search when changing stages
    if (stage !== KB.currentStage) {
      KB.filters.searchQuery = '';
      document.querySelectorAll('[data-search-input]').forEach(i => { i.value = ''; });
      // Re-render products so cleared search takes effect
      filterBarebones();
      if (KB.products.switches.length) renderProducts(getStageNumber('switches'), 'switches');
      if (KB.products.keycaps.length) renderProducts(getStageNumber('keycaps'), 'keycaps');
    }

    KB.currentStage = stage;
    // Sync pulse tracking to the new stage's current state so:
    // - incomplete stages allow pulse when selection is made
    // - already-completed stages don't pulse on entry
    previousStageComplete = isStageCompleted(stage);
    previousCartReady = canAddToCart();
    refreshUI({ stageNavigated: true });

    // Scroll to top of main content
    const mainEl = document.querySelector('.kb-builder__main');
    if (mainEl) {
      mainEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      mainEl.scrollTo({ top: 0, behavior: 'smooth' });
      requestAnimationFrame(updateScrollState);
    }

    // Mobile: page scroll is on window, not .kb-builder__main
    if (window.innerWidth <= 768) {
      const mobileHeader = document.querySelector('.kb-builder__mobile-header');
      if (mobileHeader) {
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const top = mobileHeader.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }
  }

  function updateStageVisibility() {
    const mapping = KB.stageMapping[KB.currentStage];
    if (!mapping) return;

    // Hide all stages
    Object.values(elements.stages).forEach(el => {
      if (el) el.classList.remove('kb-builder__stage--active');
    });
    document.querySelectorAll('.kb-builder__stage--sub').forEach(el => {
      el.classList.remove('kb-builder__stage--active');
    });

    // Show current stage
    let activeStageEl = null;
    switch (mapping.type) {
      case 'barebones':
        activeStageEl = elements.stages[1];
        break;
      case 'sub-stage':
        activeStageEl = document.getElementById(`kb-sub-stage-${mapping.id}`);
        break;
      case 'switches':
        activeStageEl = elements.stages[2];
        break;
      case 'keycaps':
        activeStageEl = elements.stages[3];
        break;
      case 'assembly':
        activeStageEl = elements.stages[4];
        break;
    }

    if (activeStageEl) {
      activeStageEl.classList.add('kb-builder__stage--active');

      // Move the stage nav container into the active stage's header
      if (elements.stageNav) {
        const header = activeStageEl.querySelector('.kb-builder__stage-header');
        const navSlot = header?.querySelector('.kb-builder__stage-nav');
        if (navSlot && navSlot !== elements.stageNav) {
          navSlot.replaceWith(elements.stageNav);
        } else if (header && !header.contains(elements.stageNav)) {
          header.appendChild(elements.stageNav);
        }
      }
    }
  }

  function updateNavigation() {
    if (!elements.navItems) {
      elements.navItems = elements.nav?.querySelectorAll('.kb-builder__nav-item');
    }
    if (!elements.navItems) return;

    elements.navItems.forEach(item => {
      const stage = parseInt(item.dataset.stage);
      const isActive = stage === KB.currentStage;
      const isCompleted = isStageCompleted(stage);
      let isLocked = false;
      if (stage > 1) {
        for (let j = 1; j < stage; j++) {
          if (!isStageCompleted(j)) { isLocked = true; break; }
        }
      }

      item.classList.toggle('kb-builder__nav-item--active', isActive);
      item.classList.toggle('kb-builder__nav-item--completed', isCompleted && !isActive);
      item.classList.toggle('kb-builder__nav-item--locked', isLocked);

      // Update selection text
      const selectionEl = item.querySelector('.kb-builder__nav-selection');
      if (selectionEl) {
        selectionEl.textContent = getSelectionText(stage);
      }
    });
  }

  function getSelectionText(stage) {
    const mapping = KB.stageMapping[stage];
    if (!mapping) return '';

    switch (mapping.type) {
      case 'barebones':
        return KB.selections.kit ? truncateText(KB.selections.kit.title, 25) : 'Select a kit';
      case 'sub-stage':
        if (mapping.config.type === 'he-choice') {
          if (KB.selections.heUpgrade) return 'Hall Effect Upgrade';
          if (KB.selections.subStages[mapping.id]?.selected === 'standard') return 'Standard Mechanical';
          return 'Choose option';
        }
        const subSel = KB.selections.subStages[mapping.id];
        return subSel ? truncateText(subSel.variantTitle || subSel.title, 20) : `Select ${mapping.config.label.toLowerCase()}`;
      case 'switches':
        return KB.selections.switches ? truncateText(KB.selections.switches.title, 25) : 'Select switches';
      case 'keycaps':
        return KB.selections.keycaps ? truncateText(KB.selections.keycaps.title, 25) : 'Select keycaps';
      case 'assembly':
        return KB.selections.assembly ? KB.selections.assembly.title : 'Choose option';
      default:
        return '';
    }
  }

  function isStageCompleted(stage) {
    const mapping = KB.stageMapping[stage];
    if (!mapping) return false;

    switch (mapping.type) {
      case 'barebones':
        return !!KB.selections.kit;
      case 'sub-stage':
        if (mapping.config.type === 'he-choice') {
          return KB.selections.heUpgrade || KB.selections.subStages[mapping.id]?.selected === 'standard';
        }
        return !!KB.selections.subStages[mapping.id];
      case 'switches':
        return !!KB.selections.switches;
      case 'keycaps':
        return !!KB.selections.keycaps;
      case 'assembly':
        return !!KB.selections.assembly;
      default:
        return false;
    }
  }

  function updateMobileProgress() {
    const progress = (KB.currentStage / KB.totalStages) * 100;
    if (elements.progressFill) {
      elements.progressFill.style.width = `${progress}%`;
    }
  }

  /**
   * Centralized UI refresh - call after any state change
   * @param {Object} options
   * @param {boolean} options.stagesChanged - Kit changed, HE toggled, or layout changed
   * @param {boolean} options.selectionChanged - Any selection changed (product, assembly, etc.)
   * @param {boolean} options.stageNavigated - User moved to different stage
   */
  function refreshUI(options = {}) {
    const {
      stagesChanged = false,
      selectionChanged = false,
      stageNavigated = false
    } = options;

    // If stage structure changed, rebuild everything
    if (stagesChanged) {
      recalculateStages();
    }

    // Always update these in consistent order
    updateSummary();
    updateMobileSummary();
    updateNavigation();
    updateStageVisibility();
    updateStageNav();
    updateMobileNav();
    updateMobileProgress();
    updateAllFilterToggles();
  }

  // =============================================
  // STAGE NAV BUTTONS
  // =============================================

  function updateStageNav() {
    if (!elements.stageNav) return;

    const stageReady = canProceedToNextStage();
    const cartReady = canAddToCart();

    let html = '';

    if (KB.currentStage > 1) {
      html += `<button class="kb-builder__btn kb-builder__btn--back" id="kb-btn-back">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
        Back
      </button>`;
    }

    if (KB.currentStage < KB.totalStages) {
      const readyCls = stageReady ? ' kb-builder__btn--ready' : '';
      html += `<button class="kb-builder__btn kb-builder__btn--primary${readyCls}" id="kb-btn-next" ${!stageReady ? 'disabled' : ''}>
        Continue
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/></svg>
      </button>`;
    }

    if (KB.currentStage === KB.totalStages) {
      const readyCls = cartReady ? ' kb-builder__btn--ready' : '';
      html += `<button class="kb-builder__btn kb-builder__btn--add-to-cart${readyCls}" id="kb-btn-add-to-cart" ${!cartReady ? 'disabled' : ''}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/></svg>
        Add to Cart
      </button>`;
    }

    elements.stageNav.innerHTML = html;

    // Re-bind button references
    elements.btnBack = document.getElementById('kb-btn-back');
    elements.btnNext = document.getElementById('kb-btn-next');
    elements.btnAddToCart = document.getElementById('kb-btn-add-to-cart');

    // Pulse animation on false→true transition for Continue button
    if (elements.btnNext && stageReady && !previousStageComplete) {
      elements.btnNext.classList.add('kb-builder__btn--pulse');
      elements.btnNext.addEventListener('animationend', () => {
        elements.btnNext.classList.remove('kb-builder__btn--pulse');
      }, { once: true });
    }
    previousStageComplete = stageReady;

    // Pulse animation on false→true transition for Add to Cart button
    if (elements.btnAddToCart && cartReady && !previousCartReady) {
      elements.btnAddToCart.classList.add('kb-builder__btn--pulse');
      elements.btnAddToCart.addEventListener('animationend', () => {
        elements.btnAddToCart.classList.remove('kb-builder__btn--pulse');
      }, { once: true });
    }
    previousCartReady = cartReady;

    // Attach listeners to newly created buttons
    if (elements.btnBack) {
      elements.btnBack.addEventListener('click', () => {
        goToStage(KB.currentStage - 1);
      });
    }
    if (elements.btnNext) {
      elements.btnNext.addEventListener('click', () => {
        goToStage(KB.currentStage + 1);
      });
    }
    if (elements.btnAddToCart) {
      elements.btnAddToCart.addEventListener('click', addToCart);
    }
  }

  // =============================================
  // MOBILE NAV BUTTONS (in sticky mobile header)
  // =============================================

  function updateMobileNav() {
    if (!elements.mobileNav) return;

    const stageReady = canProceedToNextStage();
    const cartReady = canAddToCart();
    const isLastStage = KB.currentStage === KB.totalStages;

    let html = '';

    // Left: Back button or spacer
    if (KB.currentStage > 1) {
      html += `<button class="kb-builder__btn--mobile kb-builder__btn--mobile-back" data-mobile-back>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
        Back
      </button>`;
    } else {
      html += '<span></span>';
    }

    // Center: Step counter
    html += `<span class="kb-builder__mobile-step">Step ${KB.currentStage} of ${KB.totalStages}</span>`;

    // Right: Next or Add to Cart
    if (isLastStage) {
      const readyCls = cartReady ? ' kb-builder__btn--ready' : '';
      html += `<button class="kb-builder__btn--mobile kb-builder__btn--mobile-cart${readyCls}" data-mobile-cart ${!cartReady ? 'disabled' : ''}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/></svg>
        Add to Cart
      </button>`;
    } else {
      const readyCls = stageReady ? ' kb-builder__btn--ready' : '';
      html += `<button class="kb-builder__btn--mobile kb-builder__btn--mobile-next${readyCls}" data-mobile-next ${!stageReady ? 'disabled' : ''}>
        Next
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/></svg>
      </button>`;
    }

    elements.mobileNav.innerHTML = html;

    // Attach click handlers
    const backBtn = elements.mobileNav.querySelector('[data-mobile-back]');
    if (backBtn) {
      backBtn.addEventListener('click', () => goToStage(KB.currentStage - 1));
    }

    const nextBtn = elements.mobileNav.querySelector('[data-mobile-next]');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (canProceedToNextStage()) goToStage(KB.currentStage + 1);
      });
    }

    const cartBtn = elements.mobileNav.querySelector('[data-mobile-cart]');
    if (cartBtn) {
      cartBtn.addEventListener('click', () => {
        if (canAddToCart()) addToCart();
      });
    }
  }

  function calculateItemCount() {
    let count = 0;
    if (KB.selections.kit) count++;

    // Count sub-stage selections (excluding HE choice)
    Object.entries(KB.selections.subStages).forEach(([id, selection]) => {
      if (selection && selection.variantId) count++;
    });

    if (KB.selections.heUpgrade && KB.selections.heUpgradeProduct) count++;
    if (KB.selections.switches) count++;
    if (KB.selections.keycaps) count++;
    if (KB.selections.assembly?.type === 'basic') count++;

    return count;
  }

  function canProceedToNextStage() {
    return isStageCompleted(KB.currentStage);
  }

  function canAddToCart() {
    // Check kit
    if (!KB.selections.kit) return false;

    // Check all required sub-stages are completed
    for (const subStage of KB.activeSubStages) {
      if (subStage.required !== false) {
        if (subStage.type === 'he-choice') {
          if (!KB.selections.heUpgrade && KB.selections.subStages[subStage.id]?.selected !== 'standard') {
            return false;
          }
        } else {
          if (!KB.selections.subStages[subStage.id]) return false;
        }
      }
    }

    // Check switches (unless HE upgrade)
    if (!KB.selections.heUpgrade && !KB.selections.switches) return false;

    // Check keycaps
    if (!KB.selections.keycaps) return false;

    // Check assembly
    if (!KB.selections.assembly) return false;

    return true;
  }

  function calculateTotal() {
    let total = 0;

    if (KB.selections.kit) total += KB.selections.kit.price;

    // Add sub-stage prices
    Object.values(KB.selections.subStages).forEach(selection => {
      if (selection && selection.price) total += selection.price;
    });

    // Add HE upgrade price if selected
    if (KB.selections.heUpgrade && KB.selections.heUpgradeProduct) {
      total += KB.selections.heUpgradeProduct.price;
    }

    if (KB.selections.switches) total += KB.selections.switches.price;
    if (KB.selections.keycaps) total += KB.selections.keycaps.price;
    if (KB.selections.assembly?.price) total += KB.selections.assembly.price;

    return total;
  }

  // =============================================
  // SUMMARY UPDATE
  // =============================================

  function updateSummary() {
    if (!elements.summaryItems) return;

    const items = [];

    if (KB.selections.kit) {
      items.push({
        name: KB.selections.kit.title,
        price: KB.selections.kit.price,
        image: KB.selections.kit.image
      });
    }

    // Add sub-stage items
    KB.activeSubStages.forEach(subStage => {
      if (subStage.type === 'he-choice') {
        if (KB.selections.heUpgrade && KB.selections.heUpgradeProduct) {
          items.push({
            name: KB.selections.heUpgradeProduct.title,
            price: KB.selections.heUpgradeProduct.price,
            image: KB.selections.heUpgradeProduct.image
          });
        }
      } else {
        const selection = KB.selections.subStages[subStage.id];
        if (selection) {
          items.push({
            name: selection.title,
            price: selection.price,
            isIncluded: selection.price === 0,
            image: selection.image
          });
        }
      }
    });

    if (KB.selections.switches) {
      items.push({
        name: KB.selections.switches.title,
        price: KB.selections.switches.price,
        image: KB.selections.switches.image
      });
    }

    if (KB.selections.keycaps) {
      items.push({
        name: KB.selections.keycaps.title,
        price: KB.selections.keycaps.price,
        image: KB.selections.keycaps.image
      });
    }

    if (KB.selections.assembly?.type === 'basic') {
      items.push({
        name: KB.selections.assembly.title,
        price: KB.selections.assembly.price
      });
    }

    if (items.length === 0) {
      elements.summaryItems.innerHTML = '<div class="kb-builder__summary-empty">Select components to start building</div>';
    } else {
      elements.summaryItems.innerHTML = items.map(item => `
        <div class="kb-builder__summary-item">
          ${item.image ? `<img class="kb-builder__summary-item-thumb" src="${item.image}" alt="" loading="lazy">` : ''}
          <span class="kb-builder__summary-item-name">${item.name}</span>
          <span class="kb-builder__summary-item-price ${item.isIncluded ? 'kb-builder__summary-item-price--included' : ''}">${item.isIncluded ? 'Included' : formatMoney(item.price)}</span>
        </div>
      `).join('');
    }

    if (elements.summaryTotal) {
      elements.summaryTotal.textContent = formatMoney(calculateTotal());
    }
  }

  // =============================================
  // MOBILE BUILD SUMMARY
  // =============================================

  function updateMobileSummary() {
    if (!elements.mobileSummaryBody) return;

    // Sync drawer body from desktop summary
    if (elements.summaryItems) {
      elements.mobileSummaryBody.innerHTML = elements.summaryItems.innerHTML;
    }

    // Count items
    let itemCount = 0;
    if (KB.selections.kit) itemCount++;
    KB.activeSubStages.forEach(sub => {
      if (sub.type === 'he-choice') {
        if (KB.selections.heUpgrade && KB.selections.heUpgradeProduct) itemCount++;
      } else if (KB.selections.subStages[sub.id]) {
        itemCount++;
      }
    });
    if (KB.selections.switches) itemCount++;
    if (KB.selections.keycaps) itemCount++;
    if (KB.selections.assembly?.type === 'basic') itemCount++;

    const total = calculateTotal();

    if (elements.mobileSummaryCount) {
      elements.mobileSummaryCount.textContent = itemCount === 1 ? '1 item' : `${itemCount} items`;
    }
    if (elements.mobileSummaryTotal) {
      elements.mobileSummaryTotal.textContent = formatMoney(total);
    }
    if (elements.mobileSummaryDrawerTotal) {
      elements.mobileSummaryDrawerTotal.textContent = formatMoney(total);
    }
  }

  let savedScrollY = 0;

  function openMobileSummary() {
    if (!elements.mobileSummary) return;
    elements.mobileSummary.classList.add('kb-mobile-summary--open');
    // Lock body scroll (iOS-safe)
    savedScrollY = window.scrollY;
    document.body.classList.add('kb-mobile-summary-locked');
    document.body.style.top = `-${savedScrollY}px`;
  }

  function closeMobileSummary() {
    if (!elements.mobileSummary) return;
    elements.mobileSummary.classList.remove('kb-mobile-summary--open');
    // Restore body scroll
    document.body.classList.remove('kb-mobile-summary-locked');
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
  }

  function attachMobileSummaryListeners() {
    if (elements.mobileSummaryTrigger) {
      elements.mobileSummaryTrigger.addEventListener('click', openMobileSummary);
    }
    if (elements.mobileSummaryClose) {
      elements.mobileSummaryClose.addEventListener('click', closeMobileSummary);
    }
    if (elements.mobileSummaryBackdrop) {
      elements.mobileSummaryBackdrop.addEventListener('click', closeMobileSummary);
    }

    // Swipe-down on handle to close
    const handle = elements.mobileSummaryDrawer?.querySelector('.kb-mobile-summary__drawer-handle');
    if (handle) {
      let startY = 0;
      handle.addEventListener('touchstart', function(e) {
        startY = e.touches[0].clientY;
      }, { passive: true });
      handle.addEventListener('touchend', function(e) {
        const endY = e.changedTouches[0].clientY;
        if (endY - startY > 40) {
          closeMobileSummary();
        }
      }, { passive: true });
    }
  }

  // =============================================
  // PRODUCT SELECTION (Base Stages)
  // =============================================

  function selectProduct(card, stage) {
    const productId = card.dataset.productId;
    const variantId = card.dataset.variantId;
    const price = parseInt(card.dataset.price);
    const title = card.dataset.title;
    const variantTitle = card.dataset.variantTitle;
    const layout = card.dataset.layout;

    const img = card.querySelector('.kb-card__image');
    const image = img ? img.src : null;

    const fullTitle = variantTitle && variantTitle !== 'Default Title'
      ? `${title} - ${variantTitle}`
      : title;

    // Determine which stage type this is
    const mapping = KB.stageMapping[KB.currentStage];
    const stageType = mapping?.type;

    if (stageType === 'barebones') {
      const hasIso = card.dataset.isoSupport === 'true';
      const hasAnsi = card.dataset.ansiSupport === 'true';

      if (hasIso && hasAnsi) {
        showLayoutPreferencePrompt(card, {
          productId, variantId, title: fullTitle, productTitle: title, price, layout, image, hasIso, hasAnsi
        });
        return;
      }

      const layoutType = hasIso ? 'iso' : (hasAnsi ? 'ansi' : null);

      // Check if this change would make existing selections incompatible
      let incompatibleItems = [];
      if (KB.selections.switches || KB.selections.keycaps) {
        const incompatibility = checkIncompatibleSelections(layout, layoutType);
        if (incompatibility.hasIncompatible) {
          incompatibleItems = incompatibility.items;
          clearIncompatibleSelections(incompatibleItems);
        }
      }

      proceedWithBareboneSelection(card, {
        productId, variantId, title: fullTitle, productTitle: title, price, layout, image
      }, layoutType);

      if (incompatibleItems.length > 0) {
        showIncompatibilityWarning(incompatibleItems);
      }
      return;
    } else if (stageType === 'switches') {
      KB.selections.switches = {
        productId,
        variantId,
        title: fullTitle,
        price,
        quantity: 1,
        image
      };
    } else if (stageType === 'keycaps') {
      KB.selections.keycaps = { productId, variantId, title: fullTitle, price, image };
    }

    // Update card selection state - use stageType to find correct container
    // Physical DOM containers are fixed: kb-products-1=barebones, kb-products-2=switches, kb-products-3=keycaps
    let container;
    if (stageType === 'barebones') {
      container = elements.productGrids[1];
    } else if (stageType === 'switches') {
      container = elements.productGrids[2];
    } else if (stageType === 'keycaps') {
      container = elements.productGrids[3];
    }

    if (container) {
      container.querySelectorAll('.kb-card').forEach(c => {
        c.classList.toggle('kb-card--selected', c === card);
      });
    }

    refreshUI({ selectionChanged: true });
  }

  /**
   * Helper to complete barebones selection after compatibility check
   */
  function proceedWithBareboneSelection(card, productData, layoutType) {
    KB.selectedLayoutPreference = layoutType;

    const previousKit = KB.selections.kit?.title;
    KB.selections.kit = {
      productId: productData.productId,
      variantId: productData.variantId,
      title: productData.title,
      productTitle: productData.productTitle,
      price: productData.price,
      layout: productData.layout,
      image: productData.image,
      layoutType: layoutType
    };

    // If kit changed, reset sub-stage selections and current stage
    if (previousKit !== productData.title) {
      KB.selections.subStages = {};
      KB.selections.heUpgrade = false;
      KB.selections.heUpgradeProduct = null;
      KB.currentStage = 1;  // Reset to barebones stage when kit changes
    }

    // Update card selection UI
    const container = elements.productGrids[1];
    if (container) {
      container.querySelectorAll('.kb-card').forEach(c => {
        c.classList.toggle('kb-card--selected', c === card);
      });
    }

    // Re-render switches with proper quantity filtering
    if (KB.products.switches.length) {
      renderProducts(getStageNumber('switches'), 'switches');
    }

    // Re-render keycaps with ISO/ANSI filter
    if (KB.products.keycaps.length) {
      renderProducts(getStageNumber('keycaps'), 'keycaps');
    }

    refreshUI({ stagesChanged: previousKit !== productData.title, selectionChanged: true });
  }

  // =============================================
  // ASSEMBLY SELECTION
  // =============================================

  function selectAssembly(type) {
    if (type === 'none') {
      KB.selections.assembly = { type: 'none', productId: null, title: 'No Assembly', price: 0 };
    } else if (type === 'basic') {
      KB.selections.assembly = {
        type: 'basic',
        productId: data.assemblyProductId,
        title: 'Keyboard Assembly & Test',
        price: data.assemblyPrice || 6000
      };
    }

    document.querySelectorAll('.kb-builder__he-option[data-assembly]').forEach(option => {
      option.classList.toggle('kb-builder__he-option--selected', option.dataset.assembly === type);
      const radio = option.querySelector('input[type="radio"]');
      if (radio) radio.checked = option.dataset.assembly === type;
    });

    refreshUI({ selectionChanged: true });
  }

  // =============================================
  // PRODUCT LOADING
  // =============================================

  async function loadProducts(type) {
    const loadingKey = `isLoading_${type}`;
    if (KB[loadingKey]) return;
    KB[loadingKey] = true;

    const stageNum = getStageNumber(type);
    const container = stageNum ? elements.productGrids[stageNum === getStageNumber('switches') ? 2 : 3] : null;

    if (!container) {
      KB[loadingKey] = false;
      return;
    }

    if (data && data[type] && Array.isArray(data[type]) && data[type].length > 0) {
      KB.products[type] = data[type];
      renderProducts(stageNum, type);
      KB[loadingKey] = false;
      return;
    }

    container.innerHTML = `
      <div class="kb-builder__loading-inline">
        <div class="kb-builder__loading-spinner-small"></div>
        <span>Loading ${type}...</span>
      </div>
    `;

    try {
      const collectionUrl = data.collections?.[type];
      if (!collectionUrl) {
        // No URL configured - show empty state instead of error
        console.warn(`No collection URL for ${type}, showing empty state`);
        container.innerHTML = `
          <div class="kb-builder__empty-state">
            <p>No ${type} available at this time.</p>
          </div>
        `;
        KB.products[type] = [];
        return;
      }

      // Simple fetch without broken pagination
      const response = await fetch(collectionUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const responseData = await response.json();
      KB.products[type] = responseData.products || [];
      renderProducts(stageNum, type);
    } catch (error) {
      console.error(`Error loading ${type}:`, error);
      container.innerHTML = `
        <div class="kb-builder__empty-state">
          <p>Error loading ${type}. Please refresh the page.</p>
        </div>
      `;
    } finally {
      KB[loadingKey] = false;
    }
  }

  function renderProducts(stage, type) {
    // Map type to container
    let container;
    if (type === 'switches') {
      container = elements.productGrids[2];
    } else if (type === 'keycaps') {
      container = elements.productGrids[3];
    } else {
      container = elements.productGrids[stage];
    }

    if (!container) return;

    container.classList.remove('kb-builder__products--loading');

    const products = KB.products[type] || [];
    const filteredProducts = filterProducts(products, type);

    if (filteredProducts.length === 0) {
      container.innerHTML = `
        <div class="kb-builder__empty-state">
          <p>No ${type === 'switches' ? 'switches' : 'keycaps'} found matching your criteria.</p>
        </div>
      `;
      return;
    }

    const cardsHtml = [];

    let requiredSwitchQty = null;
    if (type === 'switches' && KB.selections.kit) {
      requiredSwitchQty = KB.layoutRequirements[KB.selections.kit.layout] || 70;
    }

    filteredProducts.forEach(product => {
      if (type === 'switches' && requiredSwitchQty !== null) {
        const validVariants = product.variants
          .map(v => ({ variant: v, qty: parseInt(v.title, 10) }))
          .filter(v => !isNaN(v.qty) && v.qty >= requiredSwitchQty)
          .sort((a, b) => a.qty - b.qty);

        if (validVariants.length > 0) {
          const bestMatch = validVariants[0].variant;
          if (bestMatch.available || KB.filters.showOutOfStock) {
            const cardData = buildCardData(product, bestMatch, stage, type);
            cardsHtml.push(buildCardHtml(cardData, stage));
          }
        }
      } else {
        product.variants.forEach(variant => {
          if (!variant.available && !KB.filters.showOutOfStock) return;
          const cardData = buildCardData(product, variant, stage, type);
          cardsHtml.push(buildCardHtml(cardData, stage));
        });
      }
    });

    container.innerHTML = cardsHtml.join('');
    attachCardListeners(container, stage);

    // Re-apply selection state if a product is already in the build
    const currentSelection = KB.selections[type];
    if (currentSelection) {
      const selectedCard = container.querySelector(
        `[data-variant-id="${currentSelection.variantId}"]`
      );
      if (selectedCard) {
        selectedCard.classList.add('kb-card--selected');
      }
    }
  }

  function filterProducts(products, type) {
    let filtered = products.filter(product => {
      // Search filter
      if (KB.filters.searchQuery) {
        const searchLower = KB.filters.searchQuery;
        const titleMatch = product.title.toLowerCase().includes(searchLower);
        const vendorMatch = product.vendor?.toLowerCase().includes(searchLower);
        const handleMatch = product.handle?.toLowerCase().includes(searchLower);
        if (!titleMatch && !vendorMatch && !handleMatch) return false;
      }

      if (type === 'switches') {
        const switchType = getSwitchType(product);
        if (KB.filters.switchType !== 'all' && switchType !== KB.filters.switchType) {
          return false;
        }
      }

      if (type === 'keycaps' && KB.filters.keycapCompat === 'compatible' && KB.selections.kit) {
        const layout = KB.selections.kit.layout;
        const isCompatible = checkKeycapCompatibility(product, layout);
        if (!isCompatible) return false;
      }

      return true;
    });

    // Apply sorting based on KB.filters.sortBy
    filtered = sortProducts(filtered);

    // Secondary sort: compatibility (keycaps), then availability
    filtered.sort((a, b) => {
      if (type === 'keycaps' && KB.filters.keycapCompat === 'all' && KB.selections.kit) {
        const layout = KB.selections.kit.layout;
        const aCompatible = checkKeycapCompatibility(a, layout);
        const bCompatible = checkKeycapCompatibility(b, layout);
        if (aCompatible && !bCompatible) return -1;
        if (!aCompatible && bCompatible) return 1;
      }

      // Apply availability as secondary sort
      const aAvailable = a.variants.some(v => v.available);
      const bAvailable = b.variants.some(v => v.available);
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;
      return 0;
    });

    return filtered;
  }

  function sortProducts(products) {
    const sorted = [...products];
    switch (KB.filters.sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => getMinPrice(a) - getMinPrice(b));
      case 'price-high':
        return sorted.sort((a, b) => getMinPrice(b) - getMinPrice(a));
      case 'name-az':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'name-za':
        return sorted.sort((a, b) => b.title.localeCompare(a.title));
      default:
        return sorted;
    }
  }

  function getMinPrice(product) {
    if (!product.variants || product.variants.length === 0) return 0;
    return Math.min(...product.variants.map(v => v.price || 0));
  }

  function getSwitchType(product) {
    const title = product.title.toLowerCase();
    if (title.includes('linear')) return 'linear';
    if (title.includes('tactile')) return 'tactile';
    if (title.includes('clicky')) return 'clicky';
    return 'linear';
  }

  function isTrueCompatibility(value) {
    return value === true || String(value).toLowerCase() === 'true';
  }

  function checkKeycapCompatibility(product, layout) {
    const metafields = product.metafields?.keycap;
    if (!metafields) return false;

    const compatMap = {
      '60': metafields.compatibility60,
      '65': metafields.compatibility65,
      '75': metafields.compatibility75,
      'TKL': metafields.compatibilitytkl,
      '96': metafields.compatibility96,
      'Fullsize': metafields.compatibility100
    };

    const value = compatMap[layout];
    const layoutCompatible = isTrueCompatibility(value);

    if (KB.selectedLayoutPreference && layoutCompatible) {
      if (KB.selectedLayoutPreference === 'iso') {
        const isoSupport = metafields.iso;
        const isoLower = (isoSupport || '').toString().toLowerCase();
        const hasIsoSupport = isoSupport &&
          !isoLower.startsWith('no') &&
          !isoLower.includes('no support') &&
          isoLower !== 'none' &&
          isoLower !== 'n/a' &&
          isoSupport !== false &&
          isoSupport !== 'false';
        if (!hasIsoSupport) {
          return false;
        }
      } else if (KB.selectedLayoutPreference === 'ansi') {
        const ansiSupport = metafields.ansi;
        const ansiLower = (ansiSupport || '').toString().toLowerCase();
        const hasAnsiSupport = ansiSupport &&
          !ansiLower.startsWith('no') &&
          !ansiLower.includes('no support') &&
          ansiLower !== 'none' &&
          ansiLower !== 'n/a' &&
          ansiSupport !== false &&
          ansiSupport !== 'false';
        if (!hasAnsiSupport) {
          return false;
        }
      }
    }

    return layoutCompatible;
  }

  function buildCardData(product, variant, stage, type) {
    let imageUrl = null;
    if (variant.featured_image) {
      imageUrl = typeof variant.featured_image === 'object' ? variant.featured_image.src : variant.featured_image;
    } else if (product.featured_image) {
      imageUrl = typeof product.featured_image === 'object' ? product.featured_image.src : product.featured_image;
    } else if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      imageUrl = typeof firstImage === 'object' ? firstImage.src : firstImage;
    }

    const cardData = {
      productId: product.id,
      variantId: variant.id,
      title: product.title,
      variantTitle: variant.title,
      price: variant.price,
      comparePrice: variant.compare_at_price,
      available: variant.available,
      image: imageUrl,
      handle: product.handle
    };

    if (type === 'switches') {
      cardData.switchType = getSwitchType(product);
    }

    if (type === 'keycaps') {
      cardData.compatible = KB.selections.kit ? checkKeycapCompatibility(product, KB.selections.kit.layout) : true;
      cardData.ansiSupport = product.metafields?.keycap?.ansi;
      cardData.isoSupport = product.metafields?.keycap?.iso;
      cardData.material = product.metafields?.keycap?.material;
      cardData.profile = product.metafields?.keycap?.profile;
    }

    return cardData;
  }

  function buildCardHtml(cardData, stage) {
    const switchTypeClass = cardData.switchType ? `kb-card__badge--${cardData.switchType}` : '';
    const compatBadge = cardData.compatible === false
      ? '<span class="kb-card__badge kb-card__badge--incompatible">Not Compatible</span>'
      : '';

    let keycapBadges = '';
    if (cardData.ansiSupport || cardData.isoSupport) {
      if (cardData.ansiSupport && cardData.ansiSupport !== 'None') {
        const ansiClass = cardData.ansiSupport.includes('Full') ? 'compat-full' : cardData.ansiSupport.includes('Partial') ? 'compat-partial' : '';
        if (ansiClass) {
          keycapBadges += `<span class="kb-card__badge kb-card__badge--compat kb-card__badge--${ansiClass}">ANSI</span>`;
        }
      }
      if (cardData.isoSupport && cardData.isoSupport !== 'None') {
        const isoClass = cardData.isoSupport.includes('Full') ? 'compat-full' : cardData.isoSupport.includes('Partial') ? 'compat-partial' : '';
        if (isoClass) {
          keycapBadges += `<span class="kb-card__badge kb-card__badge--compat kb-card__badge--${isoClass}">ISO</span>`;
        }
      }
    }

    return `
      <div class="kb-card ${!cardData.available ? 'kb-card--unavailable' : ''}"
           data-kb-card
           data-product-id="${cardData.productId}"
           data-variant-id="${cardData.variantId}"
           data-price="${cardData.price}"
           data-title="${escapeHtml(cardData.title)}"
           data-variant-title="${escapeHtml(cardData.variantTitle)}"
           data-handle="${cardData.handle}"
           data-available="${cardData.available}"
           ${cardData.switchType ? `data-switch-type="${cardData.switchType}"` : ''}
           ${cardData.compatible !== undefined ? `data-compatible="${cardData.compatible}"` : ''}>
        <div class="kb-card__image-wrapper">
          ${cardData.image
            ? `<img class="kb-card__image" src="${getSizedImage(cardData.image, '400x')}" alt="${escapeHtml(cardData.title)}" loading="lazy">`
            : '<div class="kb-card__image-placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg></div>'
          }
          <div class="kb-card__badges">
            ${cardData.switchType ? `<span class="kb-card__badge kb-card__badge--switch-type ${switchTypeClass}">${capitalize(cardData.switchType)}</span>` : ''}
            ${keycapBadges}
            ${!cardData.available ? '<span class="kb-card__badge kb-card__badge--sold-out">Sold Out</span>' : ''}
            ${compatBadge}
          </div>
          <button class="kb-card__quick-view" type="button" data-kb-quick-view data-product-handle="${cardData.handle}" data-variant-id="${cardData.variantId}" aria-label="Quick view">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
          </button>
          <div class="kb-card__selected-indicator">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
          </div>
        </div>
        <div class="kb-card__content">
          <h3 class="kb-card__title">${escapeHtml(cardData.title)}</h3>
          ${cardData.variantTitle !== 'Default Title' ? `<p class="kb-card__variant">${escapeHtml(cardData.variantTitle)}</p>` : ''}
          ${buildKeycapInfo(cardData)}
          <div class="kb-card__price-row">
            <div class="kb-card__price">
              ${cardData.comparePrice > cardData.price ? `<span class="kb-card__price-compare">${formatMoney(cardData.comparePrice)}</span>` : ''}
              <span class="kb-card__price-current ${cardData.comparePrice > cardData.price ? 'kb-card__price-current--sale' : ''}">${formatMoney(cardData.price)}</span>
            </div>
          </div>
        </div>
        <button class="kb-card__select-btn" type="button" data-kb-select ${!cardData.available ? 'disabled' : ''}>
          ${cardData.available ? '<span class="kb-card__select-btn-text">Select</span><span class="kb-card__select-btn-text--selected">Selected</span>' : '<span>Sold Out</span>'}
        </button>
      </div>
    `;
  }

  function buildKeycapInfo(cardData) {
    let html = '';

    if (cardData.material || cardData.profile) {
      html += `<p class="kb-card__meta">`;
      if (cardData.material) html += cardData.material;
      if (cardData.material && cardData.profile) html += ' | ';
      if (cardData.profile) html += cardData.profile.replace(' Profile', '');
      html += '</p>';
    }

    return html;
  }

  // =============================================
  // EVENT LISTENERS
  // =============================================

  function attachCardListeners(container, stage) {
    if (!container) return;

    container.querySelectorAll('.kb-card').forEach(card => {
      const selectBtn = card.querySelector('[data-kb-select]');
      if (selectBtn) {
        selectBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          selectProduct(card, stage);
        });
      }

      card.addEventListener('click', (e) => {
        if (!e.target.closest('[data-kb-quick-view]') && !e.target.closest('[data-kb-select]')) {
          if (card.dataset.available !== 'false') {
            selectProduct(card, stage);
          }
        }
      });

      const quickViewBtn = card.querySelector('[data-kb-quick-view]');
      if (quickViewBtn) {
        quickViewBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          openQuickView(quickViewBtn.dataset.productHandle, quickViewBtn.dataset.variantId);
        });
      }
    });
  }

  function attachFilterListeners() {
    const layoutFilter = document.getElementById('kb-filter-layout');
    if (layoutFilter) {
      layoutFilter.addEventListener('click', (e) => {
        if (e.target.matches('.kb-builder__filter-btn')) {
          KB.filters.layout = e.target.dataset.filter;
          updateFilterButtons(layoutFilter, e.target);
          filterBarebones();
          updateAllFilterToggles();
        }
      });
    }

    const layoutTypeFilter = document.getElementById('kb-filter-layout-type');
    if (layoutTypeFilter) {
      layoutTypeFilter.addEventListener('click', (e) => {
        if (e.target.matches('.kb-builder__filter-btn')) {
          KB.filters.layoutType = e.target.dataset.filter;
          updateFilterButtons(layoutTypeFilter, e.target);
          filterBarebones();
          updateAllFilterToggles();
        }
      });
    }

    const switchFilter = document.getElementById('kb-filter-switch-type');
    if (switchFilter) {
      switchFilter.addEventListener('click', (e) => {
        if (e.target.matches('.kb-builder__filter-btn')) {
          KB.filters.switchType = e.target.dataset.filter;
          updateFilterButtons(switchFilter, e.target);
          renderProducts(getStageNumber('switches'), 'switches');
          updateAllFilterToggles();
        }
      });
    }

    const keycapFilter = document.getElementById('kb-filter-keycap-compat');
    if (keycapFilter) {
      keycapFilter.addEventListener('click', (e) => {
        if (e.target.matches('.kb-builder__filter-btn')) {
          KB.filters.keycapCompat = e.target.dataset.filter;
          updateFilterButtons(keycapFilter, e.target);
          renderProducts(getStageNumber('keycaps'), 'keycaps');
          updateAllFilterToggles();
        }
      });
    }

    document.querySelectorAll('[data-filter-oos]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        KB.filters.showOutOfStock = e.target.checked;
        document.querySelectorAll('[data-toggle-oos]').forEach(toggle => {
          const input = toggle.querySelector('input');
          if (input) input.checked = e.target.checked;
          toggle.classList.toggle('kb-builder__filter-toggle--active', e.target.checked);
        });
        document.querySelectorAll('[data-filter-oos]').forEach(cb => {
          cb.checked = e.target.checked;
        });
        filterBarebones();
        if (KB.products.switches.length) renderProducts(getStageNumber('switches'), 'switches');
        if (KB.products.keycaps.length) renderProducts(getStageNumber('keycaps'), 'keycaps');
        updateAllFilterToggles();
      });
    });

    // Search input - debounced
    let searchTimeout;
    document.querySelectorAll('[data-search-input]').forEach(input => {
      input.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          KB.filters.searchQuery = e.target.value.toLowerCase().trim();
          // Sync all search inputs
          document.querySelectorAll('[data-search-input]').forEach(i => {
            if (i !== e.target) i.value = e.target.value;
          });
          // Re-filter all stages
          filterBarebones();
          if (KB.products.switches.length) renderProducts(getStageNumber('switches'), 'switches');
          if (KB.products.keycaps.length) renderProducts(getStageNumber('keycaps'), 'keycaps');
          updateAllFilterToggles();
        }, 300);
      });
    });

    // Sort dropdown
    document.querySelectorAll('[data-sort-select]').forEach(select => {
      select.addEventListener('change', (e) => {
        KB.filters.sortBy = e.target.value;
        // Sync all sort dropdowns
        document.querySelectorAll('[data-sort-select]').forEach(s => {
          s.value = e.target.value;
        });
        // Re-render with new sort
        filterBarebones();
        if (KB.products.switches.length) renderProducts(getStageNumber('switches'), 'switches');
        if (KB.products.keycaps.length) renderProducts(getStageNumber('keycaps'), 'keycaps');
        updateAllFilterToggles();
      });
    });

    // Filter toggle (collapsible on mobile)
    document.querySelectorAll('[data-filters-toggle]').forEach(toggle => {
      toggle.addEventListener('click', () => {
        const filtersEl = toggle.closest('.kb-builder__filters');
        if (filtersEl) {
          filtersEl.classList.toggle('kb-builder__filters--expanded');
          updateFilterToggleText(filtersEl);
          initFilterScrollShadows(filtersEl);
        }
      });
    });
  }

  function updateFilterScrollShadows(btnRow) {
    const group = btnRow.closest('.kb-builder__filter-group');
    if (!group) return;
    const threshold = 2;
    group.classList.toggle('can-scroll-left', btnRow.scrollLeft > threshold);
    group.classList.toggle('can-scroll-right', btnRow.scrollLeft + btnRow.clientWidth < btnRow.scrollWidth - threshold);
  }

  function initFilterScrollShadows(filtersEl) {
    filtersEl.querySelectorAll('.kb-builder__filter-buttons').forEach(btnRow => {
      if (btnRow._scrollShadowBound) return;
      btnRow._scrollShadowBound = true;
      btnRow.addEventListener('scroll', () => updateFilterScrollShadows(btnRow), { passive: true });
      requestAnimationFrame(() => updateFilterScrollShadows(btnRow));
    });
  }

  function updateFilterToggleText(filtersEl) {
    const textEl = filtersEl.querySelector('[data-filters-toggle-text]');
    if (!textEl) return;

    let activeCount = 0;

    // Count non-"all" active filter buttons
    filtersEl.querySelectorAll('.kb-builder__filter-btn--active').forEach(btn => {
      if (btn.dataset.filter !== 'all') activeCount++;
    });

    // Count non-empty search
    const searchInput = filtersEl.querySelector('[data-search-input]');
    if (searchInput && searchInput.value.trim()) activeCount++;

    // Count non-default sort
    const sortSelect = filtersEl.querySelector('[data-sort-select]');
    if (sortSelect && sortSelect.value !== 'name-az') activeCount++;

    // Count OOS toggle
    const oosCheckbox = filtersEl.querySelector('[data-filter-oos]');
    if (oosCheckbox && oosCheckbox.checked) activeCount++;

    textEl.textContent = activeCount > 0 ? `Filters (${activeCount})` : 'Filters';
  }

  function updateAllFilterToggles() {
    document.querySelectorAll('.kb-builder__filters').forEach(filtersEl => {
      updateFilterToggleText(filtersEl);
      if (filtersEl.classList.contains('kb-builder__filters--expanded')) {
        initFilterScrollShadows(filtersEl);
      }
    });
  }

  function updateFilterButtons(container, activeBtn) {
    container.querySelectorAll('.kb-builder__filter-btn').forEach(btn => {
      btn.classList.toggle('kb-builder__filter-btn--active', btn === activeBtn);
    });
  }

  function filterBarebones() {
    const container = elements.productGrids[1];
    if (!container) return;

    const cards = container.querySelectorAll('.kb-card');
    let visibleCards = [];

    cards.forEach(card => {
      const layout = card.dataset.layout;
      const available = card.dataset.available === 'true';
      const hasIso = card.dataset.isoSupport === 'true';
      const hasAnsi = card.dataset.ansiSupport === 'true';
      const title = card.dataset.title || '';
      const variantTitle = card.dataset.variantTitle || '';
      const price = parseInt(card.dataset.price) || 0;

      const matchesLayout = KB.filters.layout === 'all' || layout === KB.filters.layout;
      const matchesStock = KB.filters.showOutOfStock || available;

      let matchesLayoutType = true;
      if (KB.filters.layoutType === 'iso') {
        matchesLayoutType = hasIso;
      } else if (KB.filters.layoutType === 'ansi') {
        matchesLayoutType = hasAnsi;
      }

      // Search filter
      let matchesSearch = true;
      if (KB.filters.searchQuery) {
        const searchLower = KB.filters.searchQuery;
        const titleMatch = title.toLowerCase().includes(searchLower);
        const variantMatch = variantTitle.toLowerCase().includes(searchLower);
        matchesSearch = titleMatch || variantMatch;
      }

      const shouldShow = matchesLayout && matchesStock && matchesLayoutType && matchesSearch;

      if (shouldShow) {
        visibleCards.push({ card, title, price, available });
      }
      card.style.display = 'none'; // Hide all initially
    });

    // Sort visible cards
    visibleCards.sort((a, b) => {
      switch (KB.filters.sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name-az':
          return a.title.localeCompare(b.title);
        case 'name-za':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    // Reorder and show visible cards
    visibleCards.forEach(({ card }) => {
      card.style.display = '';
      container.appendChild(card); // Move to end to reorder
    });

    const emptyState = document.getElementById('kb-empty-1');
    if (emptyState) {
      emptyState.style.display = visibleCards.length === 0 ? 'block' : 'none';
    }
  }

  function attachAssemblyListeners() {
    const assemblyOptions = document.getElementById('kb-assembly-options');
    if (!assemblyOptions) return;

    assemblyOptions.querySelectorAll('.kb-builder__he-option[data-assembly]').forEach(option => {
      option.addEventListener('click', () => {
        selectAssembly(option.dataset.assembly);
      });
    });

    assemblyOptions.querySelectorAll('input[name="assembly-choice"]').forEach(radio => {
      radio.addEventListener('change', () => {
        selectAssembly(radio.value);
      });
    });
  }

  function attachNavListeners() {
    if (!elements.navItems) {
      elements.navItems = elements.nav?.querySelectorAll('.kb-builder__nav-item');
    }
    if (!elements.navItems) return;

    // Nav items are recreated each time, so their listeners are fresh
    elements.navItems.forEach(item => {
      item.addEventListener('click', () => {
        const stage = parseInt(item.dataset.stage);
        if (!item.classList.contains('kb-builder__nav-item--locked')) {
          goToStage(stage);
        }
      });
    });
  }

  // =============================================
  // QUICK VIEW
  // =============================================

  async function openQuickView(handle, variantId) {
    const modal = elements.quickView;
    if (!modal) return;

    modal.classList.add('kb-quick-view--open');
    document.getElementById('kb-quick-view-loading').style.display = 'flex';
    document.getElementById('kb-quick-view-content').style.display = 'none';
    document.getElementById('kb-quick-view-actions').style.display = 'none';

    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();
      const variant = product.variants.find(v => v.id == variantId) || product.variants[0];

      populateQuickView(product, variant);
    } catch (error) {
      console.error('Error loading quick view:', error);
      closeQuickView();
    }
  }

  // Vendor logo URLs matching the theme's vendor.liquid
  const vendorLogos = {
    'GLORIOUS': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/glorious_888285c6-010d-4b93-bce7-8da2029df1ee.svg?v=1657035158',
    'KEYCHRON': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/keychron.png?v=1638978294',
    'Keychron': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/keychron.png?v=1638978294',
    'Akko': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Akko_8e832bca-10f9-42bc-a433-48eb830d0792.svg?v=1657035388',
    'Gateron': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Gateron-logo.png?v=1649110663',
    'DUCKY': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Ducky.svg?v=1621871099',
    'Skyloong': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/skyloong.svg?v=1651765250',
    'TAI-HAO': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Tai-Hao.svg?v=1626171528',
    'VARMILO': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/varmilo.png?v=1638978294',
    'Monsgeek': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/monsgeek.svg?v=1681408977',
    'Meletrix': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/meletrix_logo.svg?v=1714040263',
    'Wuque Studio': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/wuque_studio.svg?v=1714040263',
    'Chilkey': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Chilkey_Logo_New.svg?v=1734601457',
    'Royal Kludge': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/royalkludge.svg?v=1686649532',
    'WOBKEY': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/WOBKEY.svg?v=1749810742',
    'CORSAIR': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Corsair.svg?v=1621869701',
    'RAZER': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Razer.svg?v=1621869701',
    'LOGITECH': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Logitech.svg?v=1621869701',
    'HYPERX': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/HyperX.svg?v=1621869701',
    'STEELSERIES': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Steel_Series.svg?v=1626171528',
    'Computer Orbit': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Computer_Orbit.svg?v=1621869701',
    'ORBIT': 'https://cdn.shopify.com/s/files/1/0562/7713/9620/files/Computer_Orbit.svg?v=1621869701'
  };

  function populateQuickView(product, variant) {
    const content = document.getElementById('kb-quick-view-content');
    const loading = document.getElementById('kb-quick-view-loading');

    // Vendor logo
    const vendorEl = document.getElementById('kb-quick-view-vendor');
    if (vendorEl && product.vendor) {
      const logoUrl = vendorLogos[product.vendor];
      if (logoUrl) {
        vendorEl.innerHTML = `<img src="${logoUrl}" alt="${product.vendor}">`;
      } else {
        vendorEl.innerHTML = `<span class="kb-quick-view__vendor-text">${product.vendor}</span>`;
      }
    }

    document.getElementById('kb-quick-view-title').textContent = product.title;
    document.getElementById('kb-quick-view-variant').textContent =
      variant.title !== 'Default Title' ? variant.title : '';

    const currentPrice = document.getElementById('kb-quick-view-current-price');
    const comparePrice = document.getElementById('kb-quick-view-compare-price');
    currentPrice.textContent = formatMoney(variant.price);
    currentPrice.classList.toggle('kb-quick-view__price-current--sale', variant.compare_at_price > variant.price);
    comparePrice.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';

    const mainImage = document.getElementById('kb-quick-view-image');
    mainImage.src = variant.featured_image?.src || product.featured_image || '';
    mainImage.alt = product.title;

    const thumbnails = document.getElementById('kb-quick-view-thumbnails');
    thumbnails.innerHTML = product.images.map((img, i) => `
      <button class="kb-quick-view__thumbnail ${i === 0 ? 'kb-quick-view__thumbnail--active' : ''}"
              type="button" data-image-src="${img}">
        <img src="${img.replace(/\.(png|jpg|jpeg|gif|webp)/, '_100x.$1')}" alt="" loading="lazy">
      </button>
    `).join('');

    const inStock = document.querySelector('.kb-quick-view__stock--in-stock');
    const outOfStock = document.querySelector('.kb-quick-view__stock--out-of-stock');
    if (inStock) inStock.style.display = variant.available ? 'inline-flex' : 'none';
    if (outOfStock) outOfStock.style.display = variant.available ? 'none' : 'inline-flex';

    document.getElementById('kb-quick-view-description').innerHTML = product.description || '';
    document.getElementById('kb-quick-view-link').href = `/products/${product.handle}`;

    const selectBtn = document.getElementById('kb-quick-view-select');
    selectBtn.disabled = !variant.available;
    selectBtn.onclick = () => {
      const card = document.querySelector(`.kb-card[data-variant-id="${variant.id}"]`);
      if (card) {
        const stage = parseInt(card.closest('.kb-builder__stage').dataset.stage);
        selectProduct(card, stage);
      }
      closeQuickView();
    };

    loading.style.display = 'none';
    content.style.display = 'flex';
    document.getElementById('kb-quick-view-actions').style.display = 'flex';

    thumbnails.querySelectorAll('.kb-quick-view__thumbnail').forEach(thumb => {
      thumb.addEventListener('click', () => {
        mainImage.src = thumb.dataset.imageSrc;
        thumbnails.querySelectorAll('.kb-quick-view__thumbnail').forEach(t =>
          t.classList.remove('kb-quick-view__thumbnail--active'));
        thumb.classList.add('kb-quick-view__thumbnail--active');
      });
    });

    // Click main image to open lightbox zoom
    mainImage.addEventListener('click', () => {
      const lightbox = document.getElementById('kb-quick-view-lightbox');
      const lightboxImg = document.getElementById('kb-quick-view-lightbox-img');
      if (lightbox && lightboxImg) {
        lightboxImg.src = mainImage.src;
        lightboxImg.alt = mainImage.alt;
        lightbox.classList.add('kb-quick-view__lightbox--open');
      }
    });
  }

  function closeQuickView() {
    const modal = elements.quickView;
    if (modal) {
      modal.classList.remove('kb-quick-view--open');
    }
    // Also close lightbox if open
    const lightbox = document.getElementById('kb-quick-view-lightbox');
    if (lightbox) {
      lightbox.classList.remove('kb-quick-view__lightbox--open');
    }
  }

  // =============================================
  // LAYOUT PREFERENCE PROMPT
  // =============================================

  let pendingBareboneSelection = null;
  let pendingBareboneCard = null;

  function showLayoutPreferencePrompt(card, productData) {
    const modal = elements.layoutModal;
    if (!modal) {
      KB.selectedLayoutPreference = 'ansi';
      completeBareboneSelection(card, productData, 'ansi');
      return;
    }

    pendingBareboneSelection = productData;
    pendingBareboneCard = card;
    modal.classList.add('kb-layout-modal--open');
  }

  function closeLayoutModal() {
    const modal = elements.layoutModal;
    if (modal) {
      modal.classList.remove('kb-layout-modal--open');
    }
    pendingBareboneSelection = null;
    pendingBareboneCard = null;
  }

  function completeBareboneSelection(card, productData, layoutType) {
    // Check if this change would make existing selections incompatible
    let incompatibleItems = [];
    if (KB.selections.switches || KB.selections.keycaps) {
      const incompatibility = checkIncompatibleSelections(productData.layout, layoutType);
      if (incompatibility.hasIncompatible) {
        incompatibleItems = incompatibility.items;
        clearIncompatibleSelections(incompatibleItems);
      }
    }

    proceedWithCompleteBareboneSelection(card, productData, layoutType);

    if (incompatibleItems.length > 0) {
      showIncompatibilityWarning(incompatibleItems);
    }
  }

  /**
   * Helper to complete barebones selection after compatibility check (for dual-layout kits)
   */
  function proceedWithCompleteBareboneSelection(card, productData, layoutType) {
    KB.selectedLayoutPreference = layoutType;

    const previousKit = KB.selections.kit?.title;
    const previousLayout = KB.selections.kit?.layoutType;

    KB.selections.kit = {
      productId: productData.productId,
      variantId: productData.variantId,
      title: productData.title,
      productTitle: productData.productTitle,
      price: productData.price,
      layout: productData.layout,
      image: productData.image,
      layoutType: layoutType
    };

    const kitChanged = previousKit !== productData.title;
    const layoutChanged = previousLayout && previousLayout !== layoutType;

    // Reset sub-stages and current stage if kit changed
    if (kitChanged) {
      KB.selections.subStages = {};
      KB.selections.heUpgrade = false;
      KB.selections.heUpgradeProduct = null;
      KB.currentStage = 1;  // Reset to barebones stage when kit changes
    }

    // If ISO layout, auto-select standard mechanical (HE not available)
    if (layoutType === 'iso') {
      KB.selections.heUpgrade = false;
      KB.selections.heUpgradeProduct = null;
      // Find HE choice stage config and auto-complete it
      const heChoiceConfig = data.barebonesSubStages?.[productData.productTitle]?.stages?.find(s => s.type === 'he-choice');
      if (heChoiceConfig) {
        KB.selections.subStages[heChoiceConfig.id] = {
          selected: 'standard',
          title: 'Standard Mechanical'
        };
      }
    }

    // Update card selection UI
    const container = elements.productGrids[1];
    if (container) {
      container.querySelectorAll('.kb-card').forEach(c => {
        c.classList.toggle('kb-card--selected', c === card);
      });
    }

    // Re-render products that depend on layout
    if (KB.products.switches.length) {
      renderProducts(getStageNumber('switches'), 'switches');
    }
    if (KB.products.keycaps.length) {
      renderProducts(getStageNumber('keycaps'), 'keycaps');
    }

    closeLayoutModal();

    // Recalculate stages if kit or layout changed
    refreshUI({ stagesChanged: kitChanged || layoutChanged, selectionChanged: true });
  }

  function attachLayoutModalListeners() {
    const modal = elements.layoutModal;
    if (!modal) return;

    modal.querySelectorAll('[data-kb-layout-close]').forEach(el => {
      el.addEventListener('click', closeLayoutModal);
    });

    modal.querySelectorAll('[data-layout-choice]').forEach(btn => {
      btn.addEventListener('click', () => {
        const choice = btn.dataset.layoutChoice;
        if (pendingBareboneSelection && pendingBareboneCard) {
          completeBareboneSelection(pendingBareboneCard, pendingBareboneSelection, choice);
        }
      });
    });
  }

  // =============================================
  // CART INTEGRATION
  // =============================================

  async function addToCart() {
    if (!canAddToCart()) return;

    const btn = document.getElementById('kb-btn-add-to-cart');
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="kb-builder__loading-spinner-small"></span> Adding...';
    }

    const bundleId = 'kb_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    const layoutType = KB.selections.kit?.layoutType || 'ansi';
    const items = [];

    // Kit
    items.push({
      id: KB.selections.kit.variantId,
      quantity: 1,
      properties: {
        '_keyboard_bundle_id': bundleId,
        '_component_type': 'Barebones Kit',
        '_layout_type': layoutType
      }
    });

    // Sub-stage products
    Object.entries(KB.selections.subStages).forEach(([stageId, selection]) => {
      // Skip HE choice entries (no variantId) and included items (ship with kit)
      if (selection && selection.variantId && !selection.isIncluded) {
        items.push({
          id: selection.variantId,
          quantity: 1,
          properties: {
            '_keyboard_bundle_id': bundleId,
            '_component_type': selection.componentType || stageId
          }
        });
      }
    });

    // HE upgrade if selected
    if (KB.selections.heUpgrade && KB.selections.heUpgradeProduct) {
      items.push({
        id: KB.selections.heUpgradeProduct.variantId,
        quantity: 1,
        properties: {
          '_keyboard_bundle_id': bundleId,
          '_component_type': 'Hall Effect Upgrade'
        }
      });
    }

    // Switches (unless HE upgrade)
    if (!KB.selections.heUpgrade && KB.selections.switches) {
      items.push({
        id: KB.selections.switches.variantId,
        quantity: KB.selections.switches.quantity || 1,
        properties: {
          '_keyboard_bundle_id': bundleId,
          '_component_type': 'Switches'
        }
      });
    }

    // Keycaps
    items.push({
      id: KB.selections.keycaps.variantId,
      quantity: 1,
      properties: {
        '_keyboard_bundle_id': bundleId,
        '_component_type': 'Keycaps',
        '_layout_type': layoutType
      }
    });

    // Assembly
    if (KB.selections.assembly?.type === 'basic' && KB.selections.assembly.productId) {
      items.push({
        id: KB.selections.assembly.productId,
        quantity: 1,
        properties: {
          '_keyboard_bundle_id': bundleId,
          '_component_type': 'Assembly Service'
        }
      });
    }

    try {
      const response = await fetch(data.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Shopify cart error:', response.status, errorData);
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      window.location.href = data.cartUrl || '/cart';
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errBtn = document.getElementById('kb-btn-add-to-cart');
      if (errBtn) {
        errBtn.disabled = false;
        errBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/></svg> Add to Cart';
      }
      alert('There was an error adding items to your cart. Please try again.');
    }
  }

  // =============================================
  // INCOMPATIBILITY WARNINGS
  // =============================================

  let pendingKitChange = null;

  /**
   * Check if changing to a new kit/layout would make existing selections incompatible
   * @param {string} newLayout - The new kit layout (e.g., '60', '75', 'TKL')
   * @param {string} newLayoutType - The new layout type ('ansi' or 'iso')
   * @returns {Object} { hasIncompatible, items: [{ type, title, reason }] }
   */
  function checkIncompatibleSelections(newLayout, newLayoutType) {
    const incompatible = [];

    // Check switches - do we have enough for the new layout?
    if (KB.selections.switches) {
      const requiredQty = KB.layoutRequirements[newLayout] || 70;
      const selectedProduct = KB.products.switches.find(p =>
        p.variants.some(v => String(v.id) === String(KB.selections.switches.variantId))
      );

      if (selectedProduct) {
        const selectedVariant = selectedProduct.variants.find(v => String(v.id) === String(KB.selections.switches.variantId));
        const selectedQty = parseInt(selectedVariant?.title, 10) || 0;

        if (selectedQty < requiredQty) {
          incompatible.push({
            type: 'switches',
            title: KB.selections.switches.title,
            reason: `Requires ${requiredQty} switches, but selected pack only has ${selectedQty}`
          });
        }
      }
    }

    // Check keycaps - are they compatible with the new layout?
    if (KB.selections.keycaps) {
      const selectedProduct = KB.products.keycaps.find(p =>
        p.variants.some(v => String(v.id) === String(KB.selections.keycaps.variantId))
      );

      if (selectedProduct) {
        // Check layout size compatibility
        const isLayoutCompatible = checkKeycapCompatibility(selectedProduct, newLayout);

        // Check ANSI/ISO compatibility
        let isLayoutTypeCompatible = true;
        const metafields = selectedProduct.metafields?.keycap;

        if (metafields && newLayoutType) {
          if (newLayoutType === 'iso') {
            const isoSupport = metafields.iso;
            const isoLower = (isoSupport || '').toString().toLowerCase();
            isLayoutTypeCompatible = isoSupport &&
              !isoLower.startsWith('no') &&
              !isoLower.includes('no support') &&
              isoLower !== 'none' &&
              isoLower !== 'n/a' &&
              isoSupport !== false &&
              isoSupport !== 'false';
          } else if (newLayoutType === 'ansi') {
            const ansiSupport = metafields.ansi;
            const ansiLower = (ansiSupport || '').toString().toLowerCase();
            isLayoutTypeCompatible = ansiSupport &&
              !ansiLower.startsWith('no') &&
              !ansiLower.includes('no support') &&
              ansiLower !== 'none' &&
              ansiLower !== 'n/a' &&
              ansiSupport !== false &&
              ansiSupport !== 'false';
          }
        }

        if (!isLayoutCompatible) {
          incompatible.push({
            type: 'keycaps',
            title: KB.selections.keycaps.title,
            reason: `Not compatible with ${newLayout} layout`
          });
        } else if (!isLayoutTypeCompatible) {
          incompatible.push({
            type: 'keycaps',
            title: KB.selections.keycaps.title,
            reason: `Does not support ${newLayoutType.toUpperCase()} layout`
          });
        }
      }
    }

    return {
      hasIncompatible: incompatible.length > 0,
      items: incompatible
    };
  }

  function showIncompatibilityWarning(incompatibleItems) {
    const modal = elements.warningModal;
    if (!modal) return;

    const titleEl = document.getElementById('kb-warning-modal-title');
    const messageEl = document.getElementById('kb-warning-modal-message');
    const detailsEl = document.getElementById('kb-warning-modal-details');

    if (titleEl) titleEl.textContent = 'Incompatible Parts Removed';
    if (messageEl) messageEl.innerHTML = 'The following parts have been removed from your build because they are not compatible with your new keyboard. Please select replacements.';
    if (detailsEl) {
      detailsEl.innerHTML = incompatibleItems.map(item => `
        <div class="kb-warning-modal__item">
          <strong>${item.type === 'switches' ? 'Switches' : 'Keycaps'}:</strong> ${escapeHtml(item.title)}
          <br><span class="kb-warning-modal__reason">${escapeHtml(item.reason)}</span>
        </div>
      `).join('');
    }

    pendingKitChange = null;
    modal.classList.add('kb-warning-modal--open');
  }

  function closeWarningModal() {
    const modal = elements.warningModal;
    if (modal) modal.classList.remove('kb-warning-modal--open');
    pendingKitChange = null;
  }

  function clearIncompatibleSelections(incompatibleItems) {
    incompatibleItems.forEach(item => {
      if (item.type === 'switches') {
        KB.selections.switches = null;
      } else if (item.type === 'keycaps') {
        KB.selections.keycaps = null;
      }
    });
  }

  function attachWarningModalListeners() {
    const modal = elements.warningModal;
    if (!modal) return;

    modal.querySelectorAll('[data-kb-warning-close], [data-kb-warning-cancel]').forEach(el => {
      el.addEventListener('click', () => closeWarningModal());
    });

    modal.querySelectorAll('[data-kb-warning-confirm]').forEach(el => {
      el.addEventListener('click', () => closeWarningModal());
    });
  }

  // =============================================
  // MODAL HANDLERS
  // =============================================

  function attachModalListeners() {
    if (elements.quickView) {
      elements.quickView.querySelectorAll('[data-kb-quick-view-close]').forEach(el => {
        el.addEventListener('click', closeQuickView);
      });
    }

    // Lightbox close on click
    const lightbox = document.getElementById('kb-quick-view-lightbox');
    if (lightbox) {
      lightbox.addEventListener('click', () => {
        lightbox.classList.remove('kb-quick-view__lightbox--open');
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Close lightbox first if open, otherwise close quick view
        const lb = document.getElementById('kb-quick-view-lightbox');
        if (lb && lb.classList.contains('kb-quick-view__lightbox--open')) {
          lb.classList.remove('kb-quick-view__lightbox--open');
          return;
        }
        closeQuickView();
        closeLayoutModal();
        if (elements.warningModal) {
          elements.warningModal.classList.remove('kb-warning-modal--open');
        }
      }
    });
  }

  // =============================================
  // RESET BUILD
  // =============================================

  function resetBuild() {
    KB.currentStage = 1;
    KB.selections = {
      kit: null, subStages: {}, heUpgrade: false,
      heUpgradeProduct: null, switches: null, keycaps: null, assembly: null
    };
    KB.activeSubStages = [];
    KB.selectedLayoutPreference = null;
    previousStageComplete = false;
    previousCartReady = false;
    recalculateStages();
    // Deselect all card selections in DOM
    document.querySelectorAll('.kb-card--selected').forEach(c => c.classList.remove('kb-card--selected'));
    document.querySelectorAll('.kb-builder__he-option--selected').forEach(c => c.classList.remove('kb-builder__he-option--selected'));
    // Uncheck assembly radios
    document.querySelectorAll('input[name="assembly-choice"]').forEach(r => r.checked = false);
    refreshUI({ stagesChanged: true });
  }

  // =============================================
  // SCROLL SHADOW STATE
  // =============================================

  function updateScrollState() {
    const mainEl = document.querySelector('.kb-builder__main');
    if (!mainEl) return;
    const { scrollTop, scrollHeight, clientHeight } = mainEl;
    mainEl.classList.toggle('kb-scrolled', scrollTop > 8);
    mainEl.classList.toggle('kb-scroll-end', scrollTop + clientHeight >= scrollHeight - 2);
  }

  function initScrollShadows() {
    const mainEl = document.querySelector('.kb-builder__main');
    if (!mainEl) return;
    mainEl.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
  }

  // =============================================
  // INITIALIZATION
  // =============================================

  function init() {
    if (KB.isInitialized) return;
    KB.isInitialized = true;

    // Initialize stages first (needed for stage mapping before product loading)
    refreshUI({ stagesChanged: true });

    // Attach event listeners
    attachFilterListeners();
    initScrollShadows();
    attachAssemblyListeners();
    attachModalListeners();
    attachLayoutModalListeners();
    attachWarningModalListeners();
    attachMobileSummaryListeners();

    // Reset build button
    const resetBtn = document.getElementById('kb-btn-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', resetBuild);
    }

    // Attach listeners to server-rendered barebones cards
    attachCardListeners(elements.productGrids[1], 1);

    // Apply initial filter to hide out-of-stock barebones
    filterBarebones();

    // Load switches and keycaps in background
    Promise.all([
      loadProducts('switches'),
      loadProducts('keycaps')
    ]).then(() => {
      if (elements.loading) {
        elements.loading.classList.add('kb-builder__loading--hidden');
        setTimeout(() => {
          elements.loading.style.display = 'none';
        }, 300);
      }
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
